import { makePaint } from '@ck';
import type { ColorMode } from '@color';
import { Color } from '@color';
import type { Matrix } from '@geom';
import { type LineSegment, type Point, type Rect } from '@geom';
import { type Image, type ObjectFit } from '@image';
import { composeStruct } from '@strings';
import { Paragraph } from '@typography';
import type {
  StrokeCap as CKStrokeCap,
  StrokeJoin as CKStrokeJoin,
  Paint
} from 'canvaskit';
import { canvasKit } from '../resources/loadCanvasKit';
import type { CornerRadiusOpts } from './cornerRadius';
import type { Shape } from './Shape';
import { VisualCache } from './VisualCache';

export const DEFAULT_DPR = 1.0;

export interface VisualGroupOpts {
  strokeWidth: number;
  color: Color;
  id: string;
  title: string;
  desc: string;
  attrs: Record<string, string>;
  opacity: number;
  offset: Point;
}

export abstract class Visual {
  colorMode: ColorMode;
  cache: VisualCache;
  devicePixelRatio: number;

  constructor(colorMode: ColorMode, cache: VisualCache, devicePixelRatio = DEFAULT_DPR) {
    this.colorMode = colorMode;
    this.cache = cache;
    this.devicePixelRatio = devicePixelRatio;
  }

  abstract clear(color: Color): void;

  abstract save(): void;

  abstract restore(): void;

  abstract translate(p: Point): void;

  abstract scale(value: number): void;

  abstract concat(matrix: Matrix): void;

  abstract userSpaceBounds(): { bounds: Rect; scale: number };

  abstract drawLine(line: LineSegment, paint: VisualPaint | undefined): void;

  abstract drawRect(r: Rect, opts?: DrawRectOpts): void;

  abstract drawCircle(center: Point, radius: number, paint: VisualPaint): void;

  abstract drawShape(shape: Shape, paint: VisualPaint): void;

  abstract drawParagraph(
    paragraph: Paragraph,
    bounds: Rect,
    point: Point,
    opacity: number
  ): void;

  abstract drawImage(image: Image, bounds: Rect, opts?: DrawImageOpts): void;

  abstract withGroup(
    opts: Partial<VisualGroupOpts> | undefined,
    fn: (paint: VisualPaint | undefined) => void
  ): void;

  abstract withClip(
    bounds: Rect,
    fn: () => void,
    cornerRadius?: number | Partial<CornerRadiusOpts>
  ): void;
}

export interface DrawRectOpts {
  cornerRadius?: number | Partial<CornerRadiusOpts>;
  fill?: Color;
  stroke?: { color: Color; width: number };
}

export interface DrawImageOpts {
  objectFit?: ObjectFit;
  objectPosition?: Point;
  opacity?: number;
}

export enum PaintStyle {
  FILL,
  STROKE
}

export enum StrokeCap {
  BUTT,
  ROUND,
  SQUARE
}

export enum StrokeJoin {
  MITER,
  ROUND,
  BEVEL
}

export function toCkStrokeJoin(join?: StrokeJoin): CKStrokeJoin | undefined {
  if (join === undefined) {
    return undefined;
  }
  switch (join) {
    case StrokeJoin.MITER:
      return canvasKit.StrokeJoin.Miter;
    case StrokeJoin.ROUND:
      return canvasKit.StrokeJoin.Round;
    case StrokeJoin.BEVEL:
      return canvasKit.StrokeJoin.Bevel;
  }
}

export function toCkStrokeCap(cap?: StrokeCap): CKStrokeCap | undefined {
  if (cap === undefined) {
    return undefined;
  }
  switch (cap) {
    case StrokeCap.BUTT:
      return canvasKit.StrokeCap.Butt;
    case StrokeCap.ROUND:
      return canvasKit.StrokeCap.Round;
    case StrokeCap.SQUARE:
      return canvasKit.StrokeCap.Square;
  }
}

export class VisualPaint {
  #ckPaint: Paint;
  #strokeWidth?: number;
  #strokeCap?: StrokeCap;
  #color?: Color;
  #style?: PaintStyle;

  constructor() {
    this.#ckPaint = makePaint();
  }

  delete() {
    this.#ckPaint.delete();
  }

  get ckPaint(): Paint {
    return this.#ckPaint;
  }

  get color(): Color | undefined {
    return this.#color;
  }

  set color(color: Color | undefined) {
    this.#color = color;
    this.#ckPaint.setColor((color ?? Color.BLACK).ckColor);
  }

  get strokeWidth(): number | undefined {
    return this.#strokeWidth;
  }

  set strokeWidth(width: number | undefined) {
    this.#strokeWidth = width;
    this.#ckPaint.setStrokeWidth(width ?? 1);
  }

  get strokeCap(): StrokeCap | undefined {
    return this.#strokeCap;
  }

  set strokeCap(cap: StrokeCap | undefined) {
    this.#strokeCap = cap;
    switch (cap) {
      case StrokeCap.BUTT:
      case undefined:
        this.#ckPaint.setStrokeCap(canvasKit.StrokeCap.Butt);
        break;
      case StrokeCap.ROUND:
        this.#ckPaint.setStrokeCap(canvasKit.StrokeCap.Round);
        break;
      case StrokeCap.SQUARE:
        this.#ckPaint.setStrokeCap(canvasKit.StrokeCap.Square);
        break;
    }
  }

  get style(): PaintStyle | undefined {
    return this.#style;
  }

  set style(style: PaintStyle | undefined) {
    this.#style = style;
    switch (style) {
      case PaintStyle.FILL:
      case undefined:
        this.#ckPaint.setStyle(canvasKit.PaintStyle.Fill);
        break;
      case PaintStyle.STROKE:
        this.#ckPaint.setStyle(canvasKit.PaintStyle.Stroke);
        break;
    }
  }

  toString(): string {
    return composeStruct(
      [
        ['color', this.#color?.toHexRGBA()],
        ['strokeWidth', this.#strokeWidth],
        ['strokeCap', this.#strokeCap],
        ['style', this.#style]
      ],
      3
    );
  }
}

export function withVisualPaint(fn: (paint: VisualPaint) => void) {
  const paint = new VisualPaint();
  try {
    fn(paint);
  } finally {
    paint.delete();
  }
}
